import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  Button,
  Text,
  Box,
  HStack,
  UnorderedList,
  ListItem,
} from '@chakra-ui/react'
import { useState, useEffect, useCallback, useMemo } from 'react'
import {
  type ConsentState,
  type SectionStatusValue,
  acceptSection,
  formatRelative,
  readConsent,
  sectionStatus,
  writeConsent,
} from 'sacrifice-utils/consent'
import {
  CONSENT_SECTIONS,
  CONSENT_STORAGE_KEY,
  RISK_BULLETS,
  SECTION_HASHES,
  stripTrailingRule,
} from '../lib/disclaimer'
import { Scroller } from './Scroller'
import { Icon } from './Icon'

/**
 * - `gate`   — the write-gate before revealing a deposit address: all 4 sections
 *              (3 global + sign-in) must be satisfied before Continue enables.
 * - `review` — informational/view-only (e.g. opened from the footer with no
 *              wallet connected): every section is readable, globals can still be
 *              accepted, the per-campaign disclaimer is shown, and the modal
 *              closes via Done without requiring a signature.
 */
export type AgreementsModalMode = 'gate' | 'review'

export interface AgreementsModalProps {
  isOpen: boolean
  /** Dismiss without completing (the gate stays closed). */
  onClose: () => void
  /** Called once all 4 sections are satisfied and the user clicks Continue (gate mode). */
  onComplete: () => void
  /** Gate (default) or review/view-only. */
  mode?: AgreementsModalMode
  /** Whether the wallet is connected (drives the sign-in tab's button copy). */
  isConnected?: boolean
  /** Whether the user has signed in for THIS campaign — satisfies the 4th section. */
  isSignedIn?: boolean
  /** Triggers the EIP-712 sign-in signature (the 4th section). Assumes connected. */
  onSignIn?: () => void
  /** Opens the wallet-connect flow when no wallet is connected yet. */
  onConnect?: () => void
  /** True while the sign-in signature is in flight. */
  isSigning?: boolean
  /** The campaign's disclaimer text shown on the sign-in tab. */
  campaignDisclaimerText: string
}

const SIGN_IN_INDEX = CONSENT_SECTIONS.length

/** Small status badge: unread (grey minus), accepted (green check), stale (amber refresh). */
function StatusBadge({ status }: { status: SectionStatusValue }) {
  const config = {
    accepted: { color: 'success.fg', icon: 'mdi:check' },
    stale: { color: 'brand.accent', icon: 'mdi:refresh' },
    unread: { color: 'text.muted', icon: 'mdi:minus' },
  } as const
  const c = config[status]
  return (
    <Box
      as="span"
      color={c.color}
      display="inline-flex"
      alignItems="center"
      justifyContent="center"
      flexShrink={0}
      aria-hidden
    >
      <Icon icon={c.icon} width={16} height={16} />
    </Box>
  )
}

/**
 * The agreements modal. In gate mode it is the 4-section write-gate: the first
 * three sections are global (persisted via hash-versioned consent); the fourth
 * is the per-campaign sacrifice disclaimer, accepted by signing in, and Continue
 * enables only when all four are satisfied. In review mode it is the same
 * content as a read-only reference that closes via Done.
 */
export function AgreementsModal({
  isOpen,
  onClose,
  onComplete,
  mode = 'gate',
  isConnected = false,
  isSignedIn = false,
  onSignIn,
  onConnect,
  isSigning = false,
  campaignDisclaimerText,
}: AgreementsModalProps) {
  const [consent, setConsent] = useState<ConsentState>({})
  const [activeIndex, setActiveIndex] = useState(0)
  const footerBg = 'bg.band'
  const isReview = mode === 'review'

  // Refresh persisted consent whenever the modal opens.
  useEffect(() => {
    if (!isOpen) return
    setConsent(readConsent(CONSENT_STORAGE_KEY))
    setActiveIndex(0)
  }, [isOpen])

  const globalStatuses: SectionStatusValue[] = useMemo(
    () => CONSENT_SECTIONS.map(s => sectionStatus(consent[s.key], SECTION_HASHES[s.key])),
    [consent]
  )
  const signInStatus: SectionStatusValue = isSignedIn ? 'accepted' : 'unread'
  const globalAccepted = globalStatuses.filter(s => s === 'accepted').length
  const acceptedCount = globalAccepted + (isSignedIn ? 1 : 0)
  const totalCount = CONSENT_SECTIONS.length + 1
  const gateOpen = acceptedCount === totalCount

  // Accept the active global section, then advance to the next unsatisfied tab.
  const acceptActiveSection = useCallback(() => {
    const section = CONSENT_SECTIONS[activeIndex]
    if (!section) return
    const next = acceptSection(CONSENT_STORAGE_KEY, section.key, SECTION_HASHES[section.key])
    setConsent(next)
    // Advance to the first not-yet-accepted global section, else the sign-in tab.
    const nextGlobal = CONSENT_SECTIONS.findIndex(
      (s, i) =>
        i !== activeIndex && sectionStatus(next[s.key], SECTION_HASHES[s.key]) !== 'accepted'
    )
    setActiveIndex(nextGlobal === -1 ? SIGN_IN_INDEX : nextGlobal)
  }, [activeIndex])

  // Clear one section's acceptance so it can be re-read and re-affirmed.
  const resetActiveSection = useCallback(() => {
    const section = CONSENT_SECTIONS[activeIndex]
    if (!section) return
    setConsent(prev => {
      const next: ConsentState = { ...prev, [section.key]: undefined }
      writeConsent(CONSENT_STORAGE_KEY, next)
      return next
    })
  }, [activeIndex])

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl" scrollBehavior="inside" isCentered>
      <ModalOverlay backdropFilter="blur(2px)" />
      <ModalContent
        borderRadius="none"
        maxH="90vh"
        h="640px"
        display="flex"
        flexDirection="column"
        overflow="hidden"
      >
        <ModalHeader px={4} pt={4} pb={2} flexShrink={0}>
          {isReview ? 'Agreements' : 'Before you continue'}
          <Text fontSize="sm" fontWeight="normal" color="text.muted" mt={1}>
            {isReview
              ? 'The terms that govern using altar. Read any section; you can accept the global terms here too.'
              : 'Review and accept each section to reveal your deposit address.'}
          </Text>
        </ModalHeader>

        <ModalBody p={0} display="flex" flexDirection="column" flex={1} minH={0}>
          <Tabs
            index={activeIndex}
            onChange={setActiveIndex}
            variant="line"
            size="sm"
            display="flex"
            flexDirection="column"
            flex={1}
            minH={0}
          >
            <TabList flexShrink={0} overflowX="auto">
              {CONSENT_SECTIONS.map((s, i) => (
                <Tab key={s.key} gap={2} px={4} py={2} fontSize="sm" fontWeight="500">
                  <StatusBadge status={globalStatuses[i] ?? 'unread'} />
                  {s.label}
                </Tab>
              ))}
              <Tab gap={2} px={4} py={2} fontSize="sm" fontWeight="500">
                <StatusBadge status={signInStatus} />
                Sign in
              </Tab>
            </TabList>

            <TabPanels flex={1} minH={0} display="flex" flexDirection="column">
              {CONSENT_SECTIONS.map((s, i) => (
                <TabPanel
                  key={s.key}
                  p={0}
                  flex={1}
                  minH={0}
                  display="flex"
                  flexDirection="column"
                  hidden={i !== activeIndex}
                >
                  <Scroller vertical="scroll" height="100%">
                    <Box px={4} py={3}>
                      {s.key === 'disclaimer' && (
                        <UnorderedList spacing={2} mb={4}>
                          {RISK_BULLETS.map((bullet, bi) => (
                            <ListItem key={bi} fontSize="sm" fontWeight="600">
                              {bullet}
                            </ListItem>
                          ))}
                        </UnorderedList>
                      )}
                      <Text fontSize="sm" whiteSpace="pre-wrap" color="text.muted">
                        {s.displayText}
                      </Text>
                    </Box>
                  </Scroller>
                  <HStack
                    px={4}
                    py={2}
                    bg={footerBg}
                    justify="space-between"
                    flexShrink={0}
                    borderTopWidth="1px"
                  >
                    <Text fontSize="xs" color="text.muted">
                      {consent[s.key]
                        ? `Last accepted ${formatRelative(consent[s.key]!.acceptedAt)}`
                        : 'Not yet accepted'}
                    </Text>
                    <HStack spacing={2}>
                      {globalStatuses[i] !== 'unread' && (
                        <Button size="sm" variant="ghost" onClick={resetActiveSection}>
                          Reset
                        </Button>
                      )}
                      <Button
                        size="sm"
                        colorScheme={globalStatuses[i] === 'accepted' ? 'gray' : 'green'}
                        onClick={acceptActiveSection}
                        isDisabled={globalStatuses[i] === 'accepted'}
                      >
                        {globalStatuses[i] === 'accepted'
                          ? '✓ Accepted'
                          : globalStatuses[i] === 'stale'
                            ? 'Re-accept (updated)'
                            : 'I accept'}
                      </Button>
                    </HStack>
                  </HStack>
                </TabPanel>
              ))}

              {/* Sign-in tab — the per-campaign disclaimer, accepted by signature. */}
              <TabPanel
                p={0}
                flex={1}
                minH={0}
                display="flex"
                flexDirection="column"
                hidden={activeIndex !== SIGN_IN_INDEX}
              >
                <Scroller vertical="scroll" height="100%">
                  <Box px={4} py={3}>
                    <Text fontSize="sm" whiteSpace="pre-wrap" color="text.muted">
                      {stripTrailingRule(campaignDisclaimerText)}
                    </Text>
                  </Box>
                </Scroller>
                <HStack
                  px={4}
                  py={2}
                  bg={footerBg}
                  justify="space-between"
                  flexShrink={0}
                  borderTopWidth="1px"
                >
                  <Text fontSize="xs" color="text.muted">
                    {isReview
                      ? 'You accept this when you sign in to a campaign.'
                      : isSignedIn
                        ? 'Signed in — campaign disclaimer accepted'
                        : 'Sign in to accept this campaign’s disclaimer'}
                  </Text>
                  {/* In review mode the sign-in action is informational only. */}
                  {!isReview && (
                    <Button
                      size="sm"
                      colorScheme={isSignedIn ? 'gray' : 'green'}
                      onClick={isConnected ? onSignIn : onConnect}
                      isDisabled={isSignedIn}
                      isLoading={isSigning}
                      loadingText="Signing…"
                    >
                      {isSignedIn
                        ? '✓ Signed in'
                        : isConnected
                          ? 'Sign to accept'
                          : 'Connect & sign'}
                    </Button>
                  )}
                </HStack>
              </TabPanel>
            </TabPanels>
          </Tabs>
        </ModalBody>

        <ModalFooter
          px={4}
          py={2}
          justifyContent="space-between"
          flexShrink={0}
          borderTopWidth="1px"
        >
          {isReview ? (
            <>
              <Text fontSize="xs" color="text.muted">
                {globalAccepted} / {CONSENT_SECTIONS.length} global sections accepted
              </Text>
              <Button size="sm" colorScheme="green" onClick={onClose}>
                Done
              </Button>
            </>
          ) : (
            <>
              <Text
                fontSize="xs"
                color={gateOpen ? 'success.fg' : 'text.muted'}
                fontWeight={gateOpen ? '600' : '400'}
              >
                {acceptedCount} / {totalCount} accepted
              </Text>
              <HStack>
                <Button size="sm" variant="ghost" onClick={onClose}>
                  Cancel
                </Button>
                <Button size="sm" colorScheme="green" isDisabled={!gateOpen} onClick={onComplete}>
                  Continue
                </Button>
              </HStack>
            </>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
